import type { Bandeintrag, Verteilperson, Verteilstand } from "./faelle";
import { tagImBetrieb, tagPlus, wandAlsZeitpunkt, wochentagVon, zeitpunktLesen } from "./betriebstag";
import {
  ZURUECK, fahrtenAm, hindernis, istFrei, lageAm, stufeVon, waehleMitGrund,
  type Aufgabe, type Fahrt, type Hindernis, type Vorschlag,
} from "./verteilen";

/* ══════════════════════════════════════════════════════════════════
   Einteilen — die Regeln hinter der Woche der Leute (docs/65, 08.10.2026).

   Luca: *„Wir sollten trennen zwischen Kalender und reinem Planen, wo man
   die offenen Aufgaben hat, die noch keinem zugewiesen sind, und dann
   sieht, wer was hat, und das entsprechend zuordnen kann."* Variante A:
   links „Offen", rechts die Woche der Leute.

   Die Aufgaben und ihre Regeln (Hindernis, Fahrt, Vorschlag) stehen in
   `daten/verteilen.ts` und gelten unverändert. Hier steht, was dazukommt:
   die **Einsätze** der Menschen (Bediener, Fahrer, Leistung) als Balken,
   die Einsätze **ohne Person** unter „Offen", was gegen einen Menschen für
   einen Einsatz spricht, und wie die Spalte „Offen" gebündelt ist. Rein,
   ohne React — `test/einteilen.test.ts`.

   **Ein Hinweis, kein Riegel.** Urlaub sagt die Zelle laut; ob gebucht
   wird, entscheidet der Mensch, und den Riegel hält die API.
   ══════════════════════════════════════════════════════════════════ */

/** Ein Einsatz eines Menschen in der Woche (`GET /einteilen`, `einsaetze`). */
export type Einsatz = {
  belegung_id: string;
  person_id: string;
  art: string;
  von: string;
  bis: string;
  unterwegs: boolean;
  position_id: string;
  rolle: "bediener" | "leistung" | "fahrer";
  bezeichnung: string | null;
  vorgang: string | null;
  kunde: string | null;
  kennung: string | null;
  typ: string | null;
  ort: string | null;
  ortschaft: string | null;
  verlangt: string[];
  weitere: number;
};

/** Ein Einsatz, für den noch niemand gebucht ist (`offene_einsaetze`). */
export type OffenerEinsatz = {
  position_id: string;
  art: "leistung" | "bediener";
  bezeichnung: string | null;
  vorgang: string | null;
  kunde: string | null;
  kennung: string | null;
  /** Kalendertage `JJJJ-MM-TT` — die Position kennt keine Uhrzeit. */
  von: string;
  bis: string;
  zeitschaerfe: string | null;
  ort: string | null;
  ortschaft: string | null;
  entfernung: number | null;
  verlangt: string[];
};

/**
 * **Eine offene Ortsaufnahme** (`GET /einteilen`, `aufnahmen`; Paket
 * ortsaufnahme, 08.10.2026). Ohne Person steht sie unter „Offen" — am Tag
 * ihres Wunschs, unter „KW 42", wenn nur die Woche feststeht, oder unter
 * „Ohne Termin". Mit Person steht sie in deren Zeile: als Balken, wenn ein
 * Termin im Band steht (0180), sonst als Marke an ihrem Tag.
 */
export type EinteilAufnahme = {
  id: string;
  kunde: string;
  ort: string | null;
  ortschaft: string | null;
  entfernung: number | null;
  leistung: string | null;
  vorgang: string | null;
  /** `assigned_to` — leer heißt: unter „Offen". */
  person_id: string | null;
  notiz: string | null;
  termin_id: string | null;
  termin_von: string | null;
  termin_bis: string | null;
  termin_person_id: string | null;
  wunsch_von: string | null;
  wunsch_bis: string | null;
  wunsch_schaerfe: "genau" | "kw" | null;
  verlangt: string[];
};

export type Einteilstand = Verteilstand & {
  einsaetze: Einsatz[];
  offene_einsaetze: OffenerEinsatz[];
  /** Fehlt bei einer API ohne das Paket ortsaufnahme — dann keine. */
  aufnahmen?: EinteilAufnahme[];
};

/* ══════════════ Die Woche ══════════════ */

/** Die sieben Tage ab `von` — Sa·So immer dabei (Entscheidung 4). */
export function tageAb(von: string, zahl = 7): string[] {
  return Array.from({ length: zahl }, (_, i) => tagPlus(von, i));
}

/** Der Montag der Woche, in der `tag` liegt. */
export function montagZu(tag: string): string {
  return tagPlus(tag, -wochentagVon(tag));
}

export const istWochenendtag = (tag: string): boolean => wochentagVon(tag) >= 5;

/**
 * Welche Spalten ein Zeitraum im Bild belegt — erste und letzte, eingekürzt
 * auf die Woche; `null`, wenn er nicht hineinfällt. Das Ende ist exklusiv:
 * Wer bis Mitternacht belegt ist, belegt den nächsten Tag nicht.
 */
export function spaltenVon(von: string, bis: string, tage: string[]): { erste: number; letzte: number } | null {
  if (!tage.length) return null;
  const a = /^\d{4}-\d{2}-\d{2}$/.test(von) ? von : tagImBetrieb(von);
  const e = /^\d{4}-\d{2}-\d{2}$/.test(bis) ? bis : tagImBetrieb(new Date(zeitpunktLesen(bis).getTime() - 1));
  const erster = tage[0]!;
  const letzter = tage[tage.length - 1]!;
  if (e < erster || a > letzter) return null;
  return { erste: a < erster ? 0 : tage.indexOf(a), letzte: e > letzter ? tage.length - 1 : tage.indexOf(e) };
}

/** Das Fenster eines offenen Einsatzes als Zeitpunkte — 07:00 bis 18:00, wie die API bucht. */
export function fensterVon(o: Pick<OffenerEinsatz, "von" | "bis">): { von: string; bis: string } {
  return { von: wandAlsZeitpunkt(o.von, "07:00").toISOString(), bis: wandAlsZeitpunkt(o.bis, "18:00").toISOString() };
}

/* ══════════════ Was gegen einen Menschen für einen Einsatz spricht ══════════════ */

const ABWESEND = new Set(["urlaub", "krank", "schulung", "sperre"]);

/** Ein Einsatz oder ein offener Einsatz — was die Prüfung braucht. */
export type Einsatzfrage = {
  von: string; bis: string; verlangt: string[]; position_id: string; belegung_id?: string; vorgang?: string | null;
  /** Der eigene Termin einer Ortsaufnahme — er steht sich nicht selbst im Weg. */
  termin?: { von: string; bis: string } | undefined;
};

const ueberlappt = (a: { von: string; bis: string }, b: { von: string; bis: string }) =>
  zeitpunktLesen(a.von) < zeitpunktLesen(b.bis) && zeitpunktLesen(b.von) < zeitpunktLesen(a.bis);

/** Das Stück des Fensters, das an `tag` liegt (00:00–24:00 am Hof). */
function amTag(f: { von: string; bis: string }, tag: string): { von: string; bis: string } | null {
  const tagVon = wandAlsZeitpunkt(tag, "00:00");
  const tagBis = wandAlsZeitpunkt(tagPlus(tag, 1), "00:00");
  const von = new Date(Math.max(zeitpunktLesen(f.von).getTime(), tagVon.getTime()));
  const bis = new Date(Math.min(zeitpunktLesen(f.bis).getTime(), tagBis.getTime()));
  return von < bis ? { von: von.toISOString(), bis: bis.toISOString() } : null;
}

/**
 * Warum `person` für diesen Einsatz schlecht passt — **an einem Tag**, wenn
 * `tag` genannt ist, sonst über den ganzen Einsatz.
 *
 * - **abwesend**: Urlaub, krank, Schulung überschneiden den Einsatz.
 * - **einsatz**: Er hat zur selben Zeit einen anderen Einsatz — nicht an
 *   derselben Position (das ist dieser) und nicht der Einsatz selbst.
 * - **qualifikation**: Was die Position verlangt, fehlt ihm oder ist am
 *   Ende des Einsatzes abgelaufen — dieselbe Frage, die die API beim Buchen
 *   stellt (gewarnt oder gesperrt nach der Regel des Betriebs).
 */
export function einsatzHindernisse(
  e: Einsatzfrage,
  person: Verteilperson,
  band: Bandeintrag[],
  einsaetze: Einsatz[],
  tag?: string,
): Hindernis[] {
  const f = tag ? amTag(e, tag) : { von: e.von, bis: e.bis };
  const liste: Hindernis[] = [];
  if (f) {
    const weg = band.find((b) => b.person_id === person.id && ABWESEND.has(b.art) && ueberlappt(b, f));
    if (weg) liste.push({ art: "abwesend", grund: weg.art, stufe: "stopp" });
    const anderer = einsaetze.find((x) => x.person_id === person.id && x.position_id !== e.position_id
      && x.belegung_id !== e.belegung_id && ueberlappt(x, f));
    if (anderer) liste.push({ art: "einsatz", kennung: anderer.kennung, von: anderer.von, bis: anderer.bis, stufe: "acht" });
    /* Der Termin einer Ortsaufnahme sperrt die Person wie ein Einsatz (0180,
       Regel 8) — nicht der eigene, wenn gerade diese Aufnahme bewegt wird. */
    const termin = band.find((b) => b.person_id === person.id && b.art === "termin" && ueberlappt(b, f)
      && !(e.termin && b.von === e.termin.von && b.bis === e.termin.bis));
    if (termin) liste.push({ art: "termin", von: termin.von, bis: termin.bis, stufe: "acht" });
  }
  if (e.verlangt.length) {
    const amEnde = tagImBetrieb(e.bis);
    const fehlt = e.verlangt.filter((code) => !person.qualifikationen.some(
      (q) => q.code === code && (q.gueltig_bis === null || q.gueltig_bis >= amEnde)));
    if (fehlt.length) liste.push({ art: "qualifikation", codes: fehlt, stufe: "acht" });
  }
  return liste;
}

/**
 * **Wer einen offenen Einsatz nimmt — und warum** (Luca, 08.10.2026).
 *
 * Verlangt der Einsatz eine Qualifikation (Bedienerpflicht, Kran,
 * PV-Reinigung), kommen **nur Leute in Frage, die sie haben** — ohne sie nie
 * ein Vorschlag, höchstens ein Zuordnen mit Hinweis. Abwesende nie. Dann
 * dieselbe Reihenfolge wie bei den Fahrten (`waehleMitGrund`): derselbe
 * Auftrag in dem Zeitraum, als Einziger mit der Qualifikation, als Einziger
 * frei, deutlich am wenigsten zu tun. Ohne Grund kein Name.
 */
export function vorschlagFuerEinsatz(
  e: Einsatzfrage,
  leute: Verteilperson[],
  band: Bandeintrag[],
  einsaetze: Einsatz[],
  aufgaben: Aufgabe[] = [],
): Vorschlag | null {
  const tagVon = tagImBetrieb(e.von);
  const tagBis = tagImBetrieb(new Date(zeitpunktLesen(e.bis).getTime() - 1));
  const kandidaten = leute.flatMap((p) => {
    const h = einsatzHindernisse(e, p, band, einsaetze);
    if (h.some((x) => x.art === "abwesend")) return [];
    if (h.some((x) => x.art === "qualifikation")) return [];
    const auftrag = !!e.vorgang && (
      einsaetze.some((x) => x.person_id === p.id && x.vorgang === e.vorgang && ueberlappt(x, e))
      || aufgaben.some((a) => a.wer?.id === p.id && a.vorgang === e.vorgang && a.tag >= tagVon && a.tag <= tagBis));
    return [{
      p, auftrag, qualifiziert: true,
      frei: !h.some((x) => x.art === "einsatz"),
      last: einsaetze.filter((x) => x.person_id === p.id && ueberlappt(x, e)).length,
    }];
  });
  return waehleMitGrund(kandidaten, e.verlangt.length > 0);
}

/* ══════════════ Die Spalte „Offen" ══════════════ */

/** Ein Abschnitt in der Spalte „Offen": ein Tag, „liegt zurück", eine Woche (`kw:<Montag>`) oder „Ohne Termin". */
export type Offentag = { tag: string; fahrten: Fahrt[]; einsaetze: OffenerEinsatz[]; aufnahmen: EinteilAufnahme[] };

/** Der Abschnitt der Ortsaufnahmen ohne Tag und ohne Woche. */
export const OHNE_TERMIN = "ohne";
/** Der Abschnitt einer Woche: `kw:JJJJ-MM-TT` (Montag). */
export const kwGruppe = (montag: string): string => `kw:${montag}`;
export const istKwGruppe = (tag: string): boolean => tag.startsWith("kw:");

/**
 * **Was noch niemand hat** — je Tag der Woche die freien Aufgaben, je Fahrt
 * gebündelt (Auftrag, Tag, Richtung — `fahrtenAm`), davor „liegt zurück".
 * Ein Einsatz ohne Person steht am ersten Tag, an dem er im Bild ist. Tage
 * ohne etwas fallen weg.
 *
 * **Ortsaufnahmen ohne Person** (Paket ortsaufnahme) stehen an ihrem Tag,
 * unter „KW 42" oben, wenn nur die Woche feststeht, und unter „Ohne
 * Termin", wenn noch gar nichts feststeht. Was vor heute lag, steht unter
 * „liegt zurück"; was in einer anderen, späteren Woche liegt, fehlt.
 * Reihenfolge: liegt zurück · Wochen · Ohne Termin · die Tage.
 */
export function offenGruppen(
  aufgaben: Aufgabe[],
  offene: OffenerEinsatz[],
  tage: string[],
  aufnahmen: EinteilAufnahme[] = [],
  heute: string = tagImBetrieb(new Date()),
): Offentag[] {
  const frei = aufgaben.filter(istFrei);
  const liste: Offentag[] = [];
  const ohne = aufnahmen.filter((a) => aufnahmeBesitzer(a) === null);
  const abschnitt = (a: EinteilAufnahme) => aufnahmeAbschnitt(a, tage, heute);
  const zurueck = fahrtenAm(frei, ZURUECK);
  const zurueckAuf = ohne.filter((a) => abschnitt(a) === ZURUECK);
  if (zurueck.length || zurueckAuf.length) liste.push({ tag: ZURUECK, fahrten: zurueck, einsaetze: [], aufnahmen: zurueckAuf });
  const wochen = [...new Set(ohne.map(abschnitt).filter((x): x is string => !!x && istKwGruppe(x)))].sort();
  for (const w of wochen) liste.push({ tag: w, fahrten: [], einsaetze: [], aufnahmen: ohne.filter((a) => abschnitt(a) === w) });
  const ohneTag = ohne.filter((a) => abschnitt(a) === OHNE_TERMIN);
  if (ohneTag.length) liste.push({ tag: OHNE_TERMIN, fahrten: [], einsaetze: [], aufnahmen: ohneTag });
  for (const tag of tage) {
    const fahrten = fahrtenAm(frei, tag);
    const einsaetze = offene.filter((o) => {
      const sp = spaltenVon(o.von, o.bis, tage);
      return sp !== null && tage[sp.erste] === tag;
    });
    const amTag = ohne.filter((a) => abschnitt(a) === tag);
    if (fahrten.length || einsaetze.length || amTag.length) liste.push({ tag, fahrten, einsaetze, aufnahmen: amTag });
  }
  return liste;
}

/** Wie viel unter „Offen" steht: freie Aufgaben, Einsätze ohne Person, Ortsaufnahmen ohne Person. */
export const offenZahl = (gruppen: Offentag[]): number =>
  gruppen.reduce((n, g) => n + g.fahrten.reduce((m, f) => m + f.aufgaben.length, 0) + g.einsaetze.length + g.aufnahmen.length, 0);

/* ══════════════ Die Ortsaufnahmen ══════════════ */

/** Wer eine Ortsaufnahme hat: die Person des Termins, sonst `assigned_to` — `null` heißt „Offen". */
export function aufnahmeBesitzer(a: EinteilAufnahme): string | null {
  return a.termin_person_id ?? a.person_id ?? null;
}

/** Wann sie ist: der Termin, sonst der Wunsch — `null`, wenn nichts feststeht. */
export function aufnahmeFenster(a: EinteilAufnahme): { von: string; bis: string; schaerfe: "termin" | "genau" | "kw" } | null {
  if (a.termin_von && a.termin_bis) return { von: a.termin_von, bis: a.termin_bis, schaerfe: "termin" };
  if (a.wunsch_von && a.wunsch_bis && a.wunsch_schaerfe) return { von: a.wunsch_von, bis: a.wunsch_bis, schaerfe: a.wunsch_schaerfe };
  return null;
}

/**
 * **Wo eine Ortsaufnahme ohne Person unter „Offen" steht** — ein Tag der
 * Woche, `kw:<Montag>`, `OHNE_TERMIN`, `ZURUECK` oder `null` (in einer
 * späteren Woche, nicht im Bild).
 */
export function aufnahmeAbschnitt(a: EinteilAufnahme, tage: string[], heute: string): string | null {
  const f = aufnahmeFenster(a);
  if (!f) return OHNE_TERMIN;
  const erster = tagImBetrieb(f.von);
  const letzter = tagImBetrieb(new Date(zeitpunktLesen(f.bis).getTime() - 1));
  if (letzter < heute && !(tage.length && letzter >= tage[0]!)) return ZURUECK;
  if (f.schaerfe === "kw") {
    return spaltenVon(f.von, f.bis, tage) ? kwGruppe(montagZu(erster)) : null;
  }
  return tage.includes(erster) ? erster : spaltenVon(f.von, f.bis, tage) ? tage[spaltenVon(f.von, f.bis, tage)!.erste]! : null;
}

/**
 * **Wo eine zugeteilte Ortsaufnahme in der Zeile steht** — mit Termin als
 * Balken über seine Tage, sonst als Marke am ersten Tag ihres Wunschs in
 * der Woche. `null`, wenn sie nicht im Bild ist oder nichts feststeht.
 */
export function aufnahmeInZeile(
  a: EinteilAufnahme, tage: string[],
): { person: string; erste: number; letzte: number; balken: boolean } | null {
  const wer = aufnahmeBesitzer(a);
  const f = aufnahmeFenster(a);
  if (!wer || !f) return null;
  const sp = spaltenVon(f.von, f.bis, tage);
  if (!sp) return null;
  return f.schaerfe === "termin"
    ? { person: wer, ...sp, balken: true }
    : { person: wer, erste: sp.erste, letzte: sp.erste, balken: false };
}

/**
 * Die Frage hinter einer Ortsaufnahme — wie bei einem Einsatz: das Fenster
 * (Termin oder Wunsch; eine Woche ganz), was die Leistung verlangt. Ohne
 * Tag ein Augenblick am Anfang der Woche: Dann zählt nur die Qualifikation,
 * kein Urlaub und kein Einsatz.
 */
export function aufnahmeFrage(a: EinteilAufnahme, tage: string[]): Einsatzfrage {
  const f = aufnahmeFenster(a);
  const leer = wandAlsZeitpunkt(tage[0] ?? tagImBetrieb(new Date()), "07:00").toISOString();
  return {
    von: f?.von ?? leer,
    bis: f?.bis ?? leer,
    verlangt: a.verlangt,
    position_id: `aufnahme:${a.id}`,
    vorgang: a.vorgang,
    ...(a.termin_von && a.termin_bis ? { termin: { von: a.termin_von, bis: a.termin_bis } } : {}),
  };
}

/** Die Auswahl einer Ortsaufnahme — mit ihrer Frage, damit die Prüfung die Woche nicht kennen muss. */
export function aufnahmeAuswahl(a: EinteilAufnahme, tage: string[]): Auswahl {
  return { art: "aufnahme", aufnahme: a, frage: aufnahmeFrage(a, tage) };
}

/* ══════════════ Die Zeile eines Menschen ══════════════ */

/** Die Last eines Tages: offene Aufgaben (Marken) und Einsätze (Balken). */
export function lastAm(person: string, tag: string, band: Bandeintrag[], aufgaben: Aufgabe[], einsaetze: Einsatz[]) {
  const lage = lageAm(person, tag, band, aufgaben);
  const tagFenster = { von: wandAlsZeitpunkt(tag, "00:00").toISOString(), bis: wandAlsZeitpunkt(tagPlus(tag, 1), "00:00").toISOString() };
  return {
    aufgaben: lage.aufgaben,
    einsaetze: einsaetze.filter((e) => e.person_id === person && ueberlappt(e, tagFenster)).length,
  };
}

/** Abwesenheiten eines Menschen in der Woche — Urlaub, krank, Schulung — als Balken. */
export function abwesenheiten(person: string, band: Bandeintrag[]): Bandeintrag[] {
  return band.filter((b) => b.person_id === person && ABWESEND.has(b.art));
}

/** Der Einsatz, in dem eine gebundene Aufgabe steckt (Bediener an der Position, M8). */
export function einsatzZuAufgabe(a: Aufgabe, einsaetze: Einsatz[]): Einsatz | null {
  if (!a.fest || !a.wer) return null;
  return einsaetze.find((e) => e.person_id === a.wer!.id && e.kennung === a.kennung
    && (a.vorgang === null || e.vorgang === a.vorgang)) ?? null;
}

/* ══════════════ Was markiert ist und was die Zelle dazu sagt ══════════════ */

export type Auswahl =
  | { art: "aufgaben"; schluessel: string; aufgaben: Aufgabe[]; tag: string }
  | { art: "einsatz"; einsatz: Einsatz }
  | { art: "offen"; offen: OffenerEinsatz }
  | { art: "aufnahme"; aufnahme: EinteilAufnahme; frage: Einsatzfrage };

/** Die Frage hinter einer Auswahl, wenn sie ein Einsatz ist. */
export function einsatzfrage(a: Auswahl): Einsatzfrage | null {
  if (a.art === "einsatz") return a.einsatz;
  if (a.art === "aufnahme") return a.frage;
  if (a.art === "offen") {
    return { ...fensterVon(a.offen), verlangt: a.offen.verlangt, position_id: a.offen.position_id, vorgang: a.offen.vorgang };
  }
  return null;
}

/** An welchen Tagen der Woche die Auswahl liegt — dort sagt jede Zeile, ob sie passt. */
export function tageDerAuswahl(a: Auswahl, tage: string[], heute: string): string[] {
  if (a.art === "aufgaben") {
    const tag = a.tag === ZURUECK ? heute : a.tag;
    return tage.includes(tag) ? [tag] : [];
  }
  if (a.art === "aufnahme") {
    /* Mit Termin oder genauem Wunsch seine Tage; eine Woche landet an ihrem
       ersten Tag im Bild (dort steht danach die Marke); ohne Tag keiner. */
    const f = aufnahmeFenster(a.aufnahme);
    const sp = f ? spaltenVon(f.von, f.bis, tage) : null;
    if (!f || !sp) return [];
    return f.schaerfe === "kw" ? [tage[sp.erste]!] : tage.slice(sp.erste, sp.letzte + 1);
  }
  const f = a.art === "einsatz" ? a.einsatz : a.offen;
  const sp = spaltenVon(f.von, f.bis, tage);
  return sp ? tage.slice(sp.erste, sp.letzte + 1) : [];
}

export type Zellhinweis = { stufe: "gut" | "acht" | "stopp" | "hat"; hindernisse: Hindernis[] };

/**
 * **Was die Zelle eines Menschen an einem Tag sagt, solange etwas markiert ist**
 * — „passt", „Urlaub", „Einsatz zur selben Zeit", „ohne …". `null`, wenn der
 * Tag nicht zur Auswahl gehört.
 */
export function zellhinweis(
  a: Auswahl,
  person: Verteilperson,
  tag: string,
  stand: Pick<Einteilstand, "band" | "einsaetze">,
  tage: string[],
  heute: string,
): Zellhinweis | null {
  if (!tageDerAuswahl(a, tage, heute).includes(tag)) return null;
  if (a.art === "einsatz" && a.einsatz.person_id === person.id) return { stufe: "hat", hindernisse: [] };
  if (a.art === "aufnahme" && aufnahmeBesitzer(a.aufnahme) === person.id) return { stufe: "hat", hindernisse: [] };
  if (a.art === "aufgaben" && a.aufgaben.length && a.aufgaben.every((x) => x.wer?.id === person.id)) {
    return { stufe: "hat", hindernisse: [] };
  }
  const hindernisse = a.art === "aufgaben"
    ? dedupe(a.aufgaben.flatMap((x) => hindernis(x, person, stand.band)))
    : einsatzHindernisse(einsatzfrage(a)!, person, stand.band, stand.einsaetze, tag);
  return { stufe: stufeVon(hindernisse) ?? "gut", hindernisse };
}

/** Ob ein Mensch für das Markierte passt — `hat`: es gehört ihm schon. */
export type Eignung = { art: "passt" | "passt_nicht" | "hat"; hindernisse: Hindernis[] };

/**
 * **Wer passt, sieht man, sobald man etwas anfasst** (Luca, 08.10.2026).
 * Über alle Tage der Auswahl (`tageDerAuswahl`) gesammelt, was gegen den
 * Menschen spricht — Urlaub, Einsatz zur selben Zeit, fehlende
 * Qualifikation. Ohne Hindernis passt er (ruhiger Punkt am Namen), mit einem
 * wird seine Zeile gedämpft; das Wort dazu steht erst, wenn man über die
 * Zeile fährt, an der Zielzelle. Liegt die Auswahl ganz außerhalb der Woche,
 * gilt der ganze Einsatz.
 */
export function eignungVon(
  a: Auswahl,
  person: Verteilperson,
  stand: Pick<Einteilstand, "band" | "einsaetze">,
  tage: string[],
  heute: string,
): Eignung {
  const imBild = tageDerAuswahl(a, tage, heute);
  const je = imBild.map((tag) => zellhinweis(a, person, tag, stand, tage, heute)).filter((h): h is Zellhinweis => h !== null);
  if (je.some((h) => h.stufe === "hat")) return { art: "hat", hindernisse: [] };
  if (a.art === "aufnahme" && aufnahmeBesitzer(a.aufnahme) === person.id) return { art: "hat", hindernisse: [] };
  const hindernisse = je.length
    ? dedupe(je.flatMap((h) => h.hindernisse))
    : a.art === "aufgaben"
      ? dedupe(a.aufgaben.flatMap((x) => hindernis(x, person, stand.band)))
      : einsatzHindernisse(einsatzfrage(a)!, person, stand.band, stand.einsaetze);
  return { art: hindernisse.length ? "passt_nicht" : "passt", hindernisse };
}

/**
 * **Die Zielzelle** — wo das Markierte in der Zeile unter dem Zeiger landet:
 * die Spalten vom ersten bis zum letzten Tag der Auswahl in dieser Woche
 * (eine Aufgabe: ihr Tag, „liegt zurück": heute; ein Einsatz: seine Spanne).
 * `null`, wenn nichts davon im Bild ist.
 */
export function zielspanne(a: Auswahl, tage: string[], heute: string): { erste: number; letzte: number } | null {
  const imBild = tageDerAuswahl(a, tage, heute);
  if (!imBild.length) return null;
  return { erste: tage.indexOf(imBild[0]!), letzte: tage.indexOf(imBild[imBild.length - 1]!) };
}

function dedupe(liste: Hindernis[]): Hindernis[] {
  const gesehen = new Set<string>();
  return liste.filter((h) => {
    const k = h.art === "qualifikation" ? `q:${h.codes.join(",")}` : h.art;
    if (gesehen.has(k)) return false;
    gesehen.add(k);
    return true;
  });
}

/* ══════════════ Die Suche ══════════════ */

const norm = (s: string) => s.toLocaleLowerCase("de").normalize("NFKD").replace(/\p{M}/gu, "");

/** Trifft die Suche eines dieser Felder? Leer trifft alles. */
export function trifft(suche: string, ...felder: (string | null | undefined)[]): boolean {
  const q = norm(suche.trim());
  if (!q) return true;
  return felder.some((f) => !!f && norm(f).includes(q));
}

/* ══════════════ Der Rückweg ══════════════ */

export type Einsatzschritt =
  | { weg: "tauschen"; belegung_id: string; wer: string }
  | { weg: "entfernen"; belegung_id: string }
  | { weg: "buchen"; position_id: string; wer: string }
  /** Eine Ortsaufnahme zuteilen, umhängen oder mit `wer: null` zurück unter „Offen" (`POST /einteilen/aufnahme`). */
  | { weg: "aufnahme"; aufnahme_id: string; wer: string | null };

export type Einsatzantwort = {
  belegung_id?: string; vorher?: string | null; position_id?: string; wer?: string | null; weg?: string;
  aufnahme_id?: string;
};

/** Der Rückweg eines Schritts — aus dem, was die API geantwortet hat. */
export function rueckwegVon(schritt: Einsatzschritt, antwort: Einsatzantwort): Einsatzschritt | null {
  if (schritt.weg === "tauschen" && antwort.belegung_id && antwort.vorher) {
    return { weg: "tauschen", belegung_id: antwort.belegung_id, wer: antwort.vorher };
  }
  if (schritt.weg === "entfernen" && antwort.position_id && antwort.wer) {
    return { weg: "buchen", position_id: antwort.position_id, wer: antwort.wer };
  }
  if (schritt.weg === "buchen" && antwort.belegung_id) return { weg: "entfernen", belegung_id: antwort.belegung_id };
  /* Die Ortsaufnahme geht an den zurück, der sie vorher hatte — oder unter „Offen". */
  if (schritt.weg === "aufnahme" && antwort.aufnahme_id) {
    return { weg: "aufnahme", aufnahme_id: antwort.aufnahme_id, wer: antwort.vorher ?? null };
  }
  return null;
}
